import { Box, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import GroupsIcon from '@mui/icons-material/Groups';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { Logo } from '../Logo';
import { formatDateCz } from '../../utils/dateFormat';
import { logoScale } from '../../utils/logoScale';
import type { CompetitionPublic } from '../../types/results';

interface Props {
  competition: CompetitionPublic;
  shortCode: string;
  organizerLogoSrc: string | null;
}

export const ScreensaverBoard = ({ competition, shortCode, organizerLogoSrc }: Props) => {
  const qrUrl = `/api/public/competitions/${shortCode}/qr?size=720`;
  const customText = competition.boardCustomText?.trim() ?? '';
  const s = logoScale(competition.publicOrganizerLogoScale);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 3, md: 4 },
        p: { xs: 2, md: 4 },
        textAlign: 'center',
      }}
    >
      {/* Logo HasicionLine + logo poradatele */}
      <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap" justifyContent="center">
        <Logo size={96} />
        {organizerLogoSrc && (
          <Box
            component="img"
            src={organizerLogoSrc}
            alt=""
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
            sx={{
              height: { xs: 96 * s, md: 128 * s },
              maxWidth: 320 * s,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        )}
      </Stack>

      {/* Hero banner */}
      <Typography
        sx={{
          color: 'primary.main',
          fontWeight: 900,
          letterSpacing: { xs: '0.08em', md: '0.12em' },
          fontSize: { xs: '2.2rem', sm: '3rem', md: '4.2rem' },
          lineHeight: 1,
          textTransform: 'uppercase',
        }}
      >
        Výsledky online
      </Typography>

      {/* Custom text — hero info (napr. cas startu) hned pod hlavnim banerem */}
      {customText && (
        <Paper
          variant="outlined"
          sx={{
            px: { xs: 2.5, md: 4 },
            py: { xs: 1.5, md: 2 },
            maxWidth: 960,
            borderColor: 'primary.main',
            borderWidth: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Typography
            sx={{
              whiteSpace: 'pre-line',
              fontSize: { xs: '1.25rem', md: '1.75rem' },
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            {customText}
          </Typography>
        </Paper>
      )}

      {/* QR vlevo, info vpravo */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 8 }}
        alignItems="center"
        justifyContent="center"
        sx={{ width: '100%', maxWidth: 1400 }}
      >
        <Paper
          elevation={4}
          sx={{
            p: { xs: 2, md: 2.5 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={qrUrl}
            alt={`QR ${shortCode}`}
            sx={{
              width: { xs: 240, md: 400 },
              height: { xs: 240, md: 400 },
              display: 'block',
            }}
          />
          <Typography
            sx={{
              mt: 1.5,
              fontFamily: 'monospace',
              fontWeight: 800,
              fontSize: { xs: '1.25rem', md: '1.75rem' },
              color: 'primary.main',
              letterSpacing: '0.18em',
            }}
          >
            {shortCode}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: { xs: '1rem', md: '1.35rem' },
              fontWeight: 600,
              color: 'text.secondary',
            }}
          >
            Naskenujte QR a sledujte živě
          </Typography>
        </Paper>

        <Stack
          spacing={3}
          alignItems={{ xs: 'center', md: 'flex-start' }}
          textAlign={{ xs: 'center', md: 'left' }}
          // Vertikalne vystredit info sloupec vuci vysce QR karty
          justifyContent="center"
          sx={{ alignSelf: 'stretch' }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'primary.main' }}>
            <HourglassTopIcon />
            <Typography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: '0.24em', color: 'inherit' }}
            >
              Připravujeme soutěž
            </Typography>
          </Stack>
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{ lineHeight: 1.05, fontSize: { xs: '2rem', sm: '2.8rem', md: '3.6rem' } }}
          >
            {competition.name}
          </Typography>

          <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }} sx={{ mt: 1 }}>
            {competition.unitName && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <GroupsIcon sx={{ color: 'primary.main', fontSize: 36 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5, fontWeight: 700 }}>
                    POŘADATEL
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {competition.unitName}
                  </Typography>
                </Box>
              </Stack>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
              <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 36 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5, fontWeight: 700 }}>
                  DATUM
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatDateCz(competition.competitionDate)}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
              <PlaceIcon sx={{ color: 'primary.main', fontSize: 36 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5, fontWeight: 700 }}>
                  MÍSTO
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    fontStyle: competition.location ? 'normal' : 'italic',
                    color: competition.location ? 'text.primary' : 'text.disabled',
                  }}
                >
                  {competition.location || 'upřesníme'}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {/* Footer */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Divider sx={{ mb: 2, width: 320, mx: 'auto' }} />
        <Typography
          sx={{
            fontSize: { xs: '1.1rem', md: '1.6rem' },
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          Živé výsledky zajišťuje{' '}
          <Link
            href="https://www.hasicionline.cz"
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{ fontWeight: 800, color: 'primary.main' }}
          >
            www.hasicionline.cz
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};
